import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdvisorMeetingUpdate, api } from "@/services/api";
import { usePabSession } from "@/hooks/usePabSession";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { type Option as MultiSelectOption } from "@/components/MultipleSelectDropdown";
import { Avatar, AvatarFallback } from "@/components/Avatar";
import { getInitials } from "@/utils/util";
import { cn } from "@/utils/cn";
import { Copy, ExternalLink, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { Text } from "@/components/Typography";
import {
  DRAFT_SAVE_ERROR,
  NO_MEETING_AVAILABLE,
  SESSION_PREP_DRAFT_SAVED,
  SESSION_PREP_MARKED_READY,
  SESSION_READY_FAILED,
} from "@/utils/toastMessages";
import {
  formatDateForSessionPrep,
  formatTimeRangeForSessionPrep,
} from "@/pages/sessions/utils/sessionUtils";
import { validateSafeUrl } from "@/utils/util";
import { BriefingArtifact, type AgendaItem } from "@/pages/sessions/components/BriefingArtifact";


interface SessionPrepProps {
  meetingId: number;
  onClose: () => void;
  /** The PAB session this meeting actually belongs to. Optional: falls back
   *  to usePabSession()'s "active" session, which is only correct when the
   *  meeting is known to belong to that same session (see AchieverSessionsPage). */
  pabSessionId?: number;
  /**
   * "page" (default) renders the full two-column layout — Session Details,
   * Invite Details and Participants alongside the Briefing Artifact.
   * "drawer" renders only the Briefing Artifact column (Goals + Agenda), for
   * the right-side drawer opened from the Sessions tab, where the surrounding
   * card already shows the date, type and participants.
   */
  variant?: "page" | "drawer";
}

export const SessionPrep: React.FC<SessionPrepProps> = ({
  meetingId,
  onClose,
  pabSessionId,
  variant = "page",
}) => {
  const isDrawer = variant === "drawer";
  const { session } = usePabSession();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // The session id to scope every meeting-specific API call to -- prefers the
  // caller-supplied pabSessionId (the meeting's real session) over the
  // achiever's currently "active" one, which can be a different PAB cycle.
  const effectiveSessionId = pabSessionId ?? session?.id;

  // State for briefing artifact
  const [selectedGoals, setSelectedGoals] = useState<MultiSelectOption[]>([]);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [isMarkingReady, setIsMarkingReady] = useState(false);


  // Fetch meeting details
  const {
    data: meeting,
    isLoading: meetingLoading,
    error: meetingError,
  } = useQuery({
    queryKey: ["meeting", meetingId, effectiveSessionId],
    queryFn: async () => {
      return await api.pabSessions.getMeeting(effectiveSessionId!, meetingId);
    },
    enabled: !!meetingId && !!effectiveSessionId,
  });

  // Fetch all goals for the achiever
  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ["pab-sessions", "okrs", session!.id],
    queryFn: async () => {
      return api.pabSessions.getSelectedOkrs(session!.id);
    },
    enabled: !!session?.id,
  });

  // Initialize form data when meeting loads
  useEffect(() => {
    if (meeting) {
      // Initialize goals from backend
      if (meeting.sessionPrepGoals && meeting.sessionPrepGoals.length > 0) {
        setSelectedGoals(
          meeting.sessionPrepGoals
            .filter(Boolean)
            .map((title) => ({ label: title, value: title })),
        );
      }

      // Initialize agendas from backend
      if (meeting.meetingAgendas && meeting.meetingAgendas.length > 0) {
        const agendas = meeting.meetingAgendas.map((agenda, index) => ({
          id: "Agenda" + index,
          description: agenda.description || "",
          duration: agenda.duration || 0,
        }));
        setAgendaItems(agendas);
      } else {
        // Initialize with empty array if no backend data
        setAgendaItems([]);
      }
    }
  }, [meeting]);

  // Prepare goal options for dropdowns (using titles as values)
  const goalOptions = goals.map((goal) => ({
    label: goal.title,
    value: goal.title,
  }));

  // Handle copy invite
  const handleCopyInvite = () => {
    if (meeting?.meetingUrl) {
      navigator.clipboard.writeText(meeting.meetingUrl);
    }
  };

  // Handle open invite in new tab
  const handleOpenInvite = () => {
    const safeUrl = validateSafeUrl(meeting?.meetingUrl);
    if (safeUrl) {
      window.open(safeUrl, "_blank");
    }
  };

  // Handle add agenda block
  const handleAddAgendaBlock = () => {
    const newId = Date.now().toString();
    setAgendaItems([...agendaItems, { id: newId, description: "", duration: 0 }]);
  };

  // Handle update agenda item
  const handleUpdateAgendaItem = (
    id: string,
    field: "description" | "duration",
    value: string | number,
  ) => {
    setAgendaItems(
      agendaItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  // Handle goal selection change (only updates local state)
  const handleGoalsChange = (selected: MultiSelectOption[]) => {
    setSelectedGoals(selected);
  };

  const buildDraftPayload = (): AdvisorMeetingUpdate => {
    return {
      sessionPrepGoals: selectedGoals.map((g) => g.value),
      // Drop agenda blocks the user added but never filled in (e.g. via "+ Add
      // Block") — an empty description isn't a real agenda item and shouldn't
      // be persisted or sent to the backend, which rejects it as invalid.
      meetingAgendas: agendaItems
        .filter((item) => item.description.trim() !== "")
        .map((item) => ({
          description: item.description.trim(),
          duration: item.duration,
        })),
    };
  };


  const updateMeetingMutation = useMutation({
    mutationFn: async (meetingData: AdvisorMeetingUpdate) => {
      if (!effectiveSessionId || !meetingId) {
        throw new Error("Session or meeting ID is missing");
      }
      return api.pabSessions.updateMeeting(effectiveSessionId, meetingId, meetingData);
    },
  });


  const handleSaveDraft = async () => {
    if (!meetingId || !effectiveSessionId) {
      showToast({
        type: "error",
        message: NO_MEETING_AVAILABLE,
      });
      return;
    }

    try {
      const meetingUpdateData = buildDraftPayload();
      await updateMeetingMutation.mutateAsync(meetingUpdateData);

      queryClient.invalidateQueries({
        queryKey: ["meeting", meetingId, effectiveSessionId],
      });

      showToast({
        type: "success",
        message: SESSION_PREP_DRAFT_SAVED,
      });
    } catch {
      showToast({
        type: "error",
        message: DRAFT_SAVE_ERROR,
      });
    }
  };


  const handleMarkAsReady = async () => {
    if (!meeting || !effectiveSessionId || !meetingId) {
      showToast({
        type: "error",
        message: NO_MEETING_AVAILABLE,
      });
      return;
    }

    setIsMarkingReady(true);

    try {
      // SAVE DRAFT FIRST
      const meetingUpdateData = buildDraftPayload();
      await updateMeetingMutation.mutateAsync(meetingUpdateData);

      // THEN SEND EMAIL
      await api.pabSessions.markSessionPrepReady(effectiveSessionId, meetingId, {
        sessionOverview: meeting.title ?? "",
        sessionPrepGoals: meetingUpdateData.sessionPrepGoals,
        meetingAgendas: meetingUpdateData.meetingAgendas,
        sessionPrepLink: `sessions?meetingId=${meetingId}&pabSessionId=${effectiveSessionId}`,
        sessionCallLink: meeting.meetingUrl,
      });

      // REFRESH
      queryClient.invalidateQueries({
        queryKey: ["meeting", meetingId, effectiveSessionId],
      });

      showToast({
        type: "success",
        message: SESSION_PREP_MARKED_READY,
      });
    } catch (error) {
      showToast({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : SESSION_READY_FAILED,
      });
    } finally {
      setIsMarkingReady(false);
    }
  };
  

  if (meetingLoading || goalsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (meetingError || !meeting) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className="text-theme-text-error">
          Error loading session details:{" "}
          {meetingError instanceof Error ? meetingError.message : "Meeting not found"}
        </Text>
      </div>
    );
  }

  const participants = meeting.participants || [];

  const ParticipantRow = ({ name, role }: { name: string; role: string }) => (
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        <AvatarFallback className="bg-primary-100 text-primary-600 text-sm font-semibold">
          {getInitials(name).slice(0, 2)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1">
        <Text as="p" className="text-md font-medium text-gray-900 dark:text-white">
          {name}
        </Text>
        <Text as="p" className="text-sm text-gray-600 dark:text-pab-dark-subtle-text">
          {role}
        </Text>
      </div>
    </div>
  );

  const participantRoleLabels: Record<string, string> = {
    ACHIEVER: "Achiever",
    ADVISOR: "Advisor",
  };

  const participantRows = participants.map((p) => ({
    id: p.id,
    name: p.fullName,
    role: participantRoleLabels[p.role] ?? p.role,
  }));

  return (
    // h-full + overflow-y-auto (not min-h-screen) -- when both Expert Program
    // and PAB tabs exist, SessionsPage wraps the active tab in a height-
    // constrained, overflow-hidden container, and this is returned directly
    // into it (not wrapped in AchieverSessionsPage's own scrollable list
    // container). min-h-screen assumes it can grow the whole document and
    // let the browser scroll, which only holds in the single-tab case --
    // this matches the pattern the list view already uses successfully.
    /* In the drawer, inherit the panel's own surface. The page variant paints a
       neutral-50 backdrop so its cards read as raised, but inside a drawer that
       backdrop becomes a grey patch sitting on the panel's white — which is the
       mismatched block visible in light mode. */
    <div
      className={cn(
        "h-full overflow-y-auto",
        isDrawer ? "bg-transparent" : "bg-neutral-50 dark:bg-surface-page-bg",
      )}
    >
      <div className={cn("w-full space-y-6", isDrawer ? "px-5 py-5" : "px-4 py-6 sm:px-6")}>
        <div className={cn("space-y-6", isDrawer ? "" : "max-w-[1368px] mx-auto")}>
          {/* Breadcrumbs — the drawer has its own header/close affordance */}
          {!isDrawer && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-pab-dark-subtle-text">
              <button onClick={onClose} className="hover:text-gray-900 dark:hover:text-white flex items-center gap-2">
                <Text as="span">Sessions</Text>
              </button>
              <ChevronRight className="h-4 w-4" />
              <Text as="span" className="text-gray-900 dark:text-white font-medium">
                Session Prep
              </Text>
            </div>
          )}

          {/* Main Content — two columns on the page, artifact-only in the drawer */}
          <div className={cn("grid grid-cols-1 gap-8", isDrawer ? "" : "lg:grid-cols-3")}>
            {/* Left Column — page only */}
            <div className={cn("space-y-6 lg:col-span-1", isDrawer && "hidden")}>
              {/* Session Details Card */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg py-6 px-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Session Details</h3>
                <div className="space-y-3">
                  <div>
                    <Text as="span" className="text-sm text-gray-600 dark:text-pab-dark-subtle-text">
                      Date:
                    </Text>
                    <Text as="p" className="text-md text-gray-900 dark:text-white mt-1">
                      {formatDateForSessionPrep(meeting.startTime)}
                    </Text>
                  </div>
                  <div>
                    <Text as="span" className="text-sm text-gray-600 dark:text-pab-dark-subtle-text">
                      Time:
                    </Text>
                    <Text as="p" className="text-md text-gray-900 dark:text-white mt-1">
                      {formatTimeRangeForSessionPrep(meeting.startTime, meeting.endTime)}
                    </Text>
                  </div>
                  <div>
                    <Text as="span" className="text-sm text-gray-600 dark:text-pab-dark-subtle-text">
                      Type:
                    </Text>
                    <Text as="p" className="text-md text-gray-900 dark:text-white mt-1">
                      {meeting?.meetingFormat === "GROUP" ? "GROUP" : "1:1"}
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Invite Details Card */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Invite Details</h3>
                <div className="flex flex-row flex-wrap gap-3 w-full">
                  <Button
                    variant="outlinePrimary"
                    size="md"
                    onClick={handleCopyInvite}
                    leftIcon={<Copy className="h-5 w-5" />}
                    className="session-outline-btn px-6 py-4 text-sm font-semibold w-[208px] h-[36px]"
                  >
                    <Text className="font-inter text-lg">Copy Invite</Text>
                  </Button>
                  <Button
                    variant="outlinePrimary"
                    size="sm"
                    onClick={handleOpenInvite}
                    className="session-outline-btn w-[36px] h-[36px]"
                  >
                    <Text className="font-inter text-sm text-primary-650">
                      <ExternalLink className="h-5 w-5 font-inter text-primary-650" />
                    </Text>
                  </Button>
                </div>
              </Card>

              {/* Participants Card */}
              <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Participants</h3>
                <div className="space-y-4">
                  {participantRows.map((p) => (
                    <ParticipantRow key={p.id} name={p.name} role={p.role} />
                  ))}
                </div>
              </Card>
            </div>

            {/* Right Column - Briefing Artifact */}
            <div className={cn(isDrawer ? "" : "lg:col-span-2")}>
              {/* Deliberately NOT <Card> in the drawer.
                  Card carries a `.card` CSS rule that sets background, border,
                  padding and shadow as real declarations — utility classes like
                  bg-transparent don't reliably beat it, so the card's white
                  background kept showing through as a patch on the drawer's own
                  panel. A plain div has nothing to override. */}
              <BriefingArtifact
                isDrawer={isDrawer}
                sessionTitle={meeting?.title}
                goalOptions={goalOptions}
                selectedGoals={selectedGoals}
                onGoalsChange={handleGoalsChange}
                agendaItems={agendaItems}
                onAddAgendaBlock={handleAddAgendaBlock}
                onUpdateAgendaItem={handleUpdateAgendaItem}
                onSaveDraft={handleSaveDraft}
                onMarkAsReady={handleMarkAsReady}
                isMarkingReady={isMarkingReady}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
